import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { Button } from './Button';
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
  }, [location]);
  const activities = [{
    name: 'Badminton',
    path: '/activities/badminton'
  }, {
    name: 'Multi-Sports',
    path: '/activities/multi-sports'
  }, {
    name: 'Netball',
    path: '/activities/netball'
  }];
  return <header data-component="COMP-01-Header" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-sm py-3' : 'bg-navy-50 py-4'}`} data-id="element-199">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-200">
        <div className="flex justify-between items-center" data-id="element-201">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-id="element-202">
            <img src="https://cdn.magicpatterns.com/uploads/x5wZymbCzDBTuYMxy9hThA/cropped-Bright-Star-Sports-Home-Page-01.png" alt="Bright Star Sports" className="h-10 md:h-12 w-auto" data-id="element-203" />
          </Link>

          {/* Desktop Nav */}
          <nav aria-label="Main navigation" className="hidden md:flex items-center space-x-8" data-id="element-204">
            <Link to="/" className="font-medium text-navy-700 hover:text-brand-blue transition-colors" data-id="element-205">
              Home
            </Link>

            <Link to="/about" className="font-medium text-navy-700 hover:text-brand-blue transition-colors" data-id="element-206">
              About
            </Link>

            {/* Dropdown */}
            <div className="relative" onMouseEnter={() => setIsDropdownOpen(true)} onMouseLeave={() => setIsDropdownOpen(false)} data-id="element-207">
              <button className="flex items-center font-medium text-navy-700 hover:text-brand-blue transition-colors" aria-expanded={isDropdownOpen} aria-haspopup="true" data-id="element-208">
                Activities <ChevronDownIcon className="w-4 h-4 ml-1" data-id="element-209" />
              </button>

              <AnimatePresence data-id="element-210">
                {isDropdownOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-navy-50 py-2 overflow-hidden" data-id="element-211">
                    {activities.map(activity => <Link key={activity.name} to={activity.path} className="block px-4 py-2 text-navy-700 hover:bg-navy-50 hover:text-brand-blue font-medium transition-colors" data-id="element-212">
                        {activity.name}
                      </Link>)}
                  </motion.div>}
              </AnimatePresence>
            </div>

            <Link to="/become-a-coach" className="font-medium text-navy-700 hover:text-brand-blue transition-colors" data-id="element-213">
              Become a Coach
            </Link>
          </nav>

          {/* CTA & Mobile Toggle */}
          <div className="flex items-center space-x-4" data-id="element-214">
            <div className="hidden md:block" data-id="element-215">
              <Button href="/book" variant="primary" size="sm" data-id="element-216">
                Book Now
              </Button>
            </div>

            <button className="md:hidden p-2 rounded-lg text-navy-900" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu" aria-expanded={isMobileMenuOpen} data-id="element-217">
              <MenuIcon className="w-6 h-6" data-id="element-218" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      <AnimatePresence data-id="element-219">
        {isMobileMenuOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-navy-900/60 backdrop-blur-sm z-50 md:hidden" onClick={() => setIsMobileMenuOpen(false)} data-id="element-220" />
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed top-0 right-0 bottom-0 w-[80%] max-w-sm bg-white z-50 p-6 flex flex-col md:hidden shadow-2xl" data-id="element-221">
              <div className="flex justify-between items-center mb-8" data-id="element-222">
                <span className="font-display font-bold text-xl text-navy-900" data-id="element-223">
                  Menu
                </span>
                <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-navy-500 hover:text-navy-900 bg-navy-50 rounded-full" aria-label="Close menu" data-id="element-224">
                  <XIcon className="w-5 h-5" data-id="element-225" />
                </button>
              </div>

              <nav aria-label="Mobile navigation" className="flex flex-col space-y-6 flex-1" data-id="element-226">
                <Link to="/" className="text-xl font-bold text-navy-900" data-id="element-227">
                  Home
                </Link>

                <Link to="/about" className="text-xl font-bold text-navy-900" data-id="element-228">
                  About
                </Link>

                <div className="space-y-4" data-id="element-229">
                  <span className="text-xl font-bold text-navy-900" data-id="element-230">
                    Activities
                  </span>
                  <div className="flex flex-col space-y-3 pl-4 border-l-2 border-navy-100" data-id="element-231">
                    {activities.map(activity => <Link key={activity.name} to={activity.path} className="text-lg text-navy-600 hover:text-brand-blue" data-id="element-232">
                        {activity.name}
                      </Link>)}
                  </div>
                </div>

                <Link to="/become-a-coach" className="text-xl font-bold text-navy-900" data-id="element-233">
                  Become a Coach
                </Link>
              </nav>

              <div className="mt-auto pt-8 border-t border-navy-100" data-id="element-234">
                <Button href="/book" variant="primary" className="w-full" data-id="element-235">
                  Book Now
                </Button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </header>;
}